import type { ReactNode } from "react"
import ModuleLayout, { type BreadcrumbItem, type ModuleMetric } from "../admin/ModuleLayout"
import { EntityDetailNavLayout, type EntityDetailNavGroup } from "../admin/EntityDetailNav"

export type CourseTabKey = "info" | "contenido" | "equipo" | "programas"

const COURSE_TAB_LABELS: Record<CourseTabKey, string> = {
  info: "Información",
  contenido: "Contenido",
  equipo: "Instructores y prerrequisitos",
  programas: "Rutas académicas",
}

interface CourseShellProps {
  mode: "create" | "edit"
  activeTab: CourseTabKey
  onTabChange: (tab: CourseTabKey) => void
  breadcrumbs: BreadcrumbItem[]
  title: string
  subtitle: string
  metrics?: ModuleMetric[]
  headerActions?: ReactNode
  children: ReactNode
}

/**
 * Shell compartido para crear/ver/editar un curso (mismo patrón que
 * SeedbedShell/GroupManagerPage): al crear se muestra la misma barra de tabs
 * que al ver/editar, con solo "Información" habilitada y el resto
 * visible-pero-bloqueado hasta guardar.
 */
export function CourseShell({
  mode,
  activeTab,
  onTabChange,
  breadcrumbs,
  title,
  subtitle,
  metrics,
  headerActions,
  children,
}: CourseShellProps) {
  const locked = (key: CourseTabKey) => mode === "create" && key !== "info"
  const groups: EntityDetailNavGroup<CourseTabKey>[] = [
    { label: "GENERAL", items: [
      { key: "info", label: COURSE_TAB_LABELS.info, locked: locked("info") },
      { key: "equipo", label: COURSE_TAB_LABELS.equipo, locked: locked("equipo") },
    ] },
    { label: "DISTRIBUCIÓN", items: [
      { key: "contenido", label: COURSE_TAB_LABELS.contenido, locked: locked("contenido") },
      { key: "programas", label: COURSE_TAB_LABELS.programas, locked: locked("programas") },
    ] },
  ]

  return (
    <ModuleLayout
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      tabs={[{ label: "Cursos", to: "/academy/courses", active: true }]}
      metrics={metrics ?? []}
      actions={headerActions}
    >
      <EntityDetailNavLayout groups={groups} activeKey={activeTab} onChange={(key) => !locked(key) && onTabChange(key)}>
        {children}
      </EntityDetailNavLayout>
    </ModuleLayout>
  )
}
